import { useEffect, useState } from "react";

import { catalog, type ApiProduct, type ApiProductImage, type ApiSku } from "@/lib/api";
import { primaryImage } from "@/lib/primary-image";

export { primaryImage };

/** Two colour names are the same finish however either was capitalised. */
function sameColor(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * The images to show for one colourway, resolved most-specific-first:
 *
 *   (top, leg) → (top, NULL) → (NULL, NULL)
 *
 * The ladder is the point. Nobody has to photograph all twelve combinations
 * before the page works: shoot the four tops, and every leg colour inherits
 * them until somebody shoots the pairing. A shopper switching to a colourway
 * nobody has photographed sees the product, not an empty frame.
 *
 * Additional photos are on no rung: see galleryForSelection.
 *
 * Mirrors Product.ImagesForColor in services/api/internal/models/catalog.go.
 */
export function imagesForColor(
  product: Pick<ApiProduct, "images">,
  top: string | undefined,
  leg?: string | undefined,
): ApiProductImage[] {
  const images = (product.images ?? []).filter((i) => !i.isAdditional);
  const exact = images.filter((i) => sameColor(i.topColor, top) && sameColor(i.legColor, leg));
  if (exact.length > 0) return exact;
  // Only a rung when a leg colour was actually asked for; without the guard
  // this is the same filter as above and would double up on a product that has
  // no leg axis at all.
  if (leg) {
    const byTop = images.filter((i) => sameColor(i.topColor, top) && !i.legColor);
    if (byTop.length > 0) return byTop;
  }
  return images.filter((i) => !i.topColor && !i.legColor);
}

/**
 * One catalogue fetch, shared by every grid on the site.
 *
 * The homepage rows and the catalogue page all want the same thing — a
 * thumbnail per slug — and each mounting its own request would mean three
 * copies of the catalogue on the way to a landing page.
 */
let inflight: Promise<Map<string, ApiProductImage>> | null = null;

function loadThumbnails(): Promise<Map<string, ApiProductImage>> {
  inflight ??= catalog
    .list()
    .then((r) => {
      const byslug = new Map<string, ApiProductImage>();
      for (const p of r.products) {
        const image = primaryImage(p);
        if (image) byslug.set(p.slug, image);
      }
      return byslug;
    })
    // A catalogue outage must not blank the grid. An empty map leaves every
    // card on the photography that shipped with the build.
    .catch(() => new Map<string, ApiProductImage>());
  return inflight;
}

/**
 * Uploaded thumbnails by slug, empty until the catalogue loads.
 *
 * Deliberately empty during prerender: the grids render the bundled
 * photography into the static HTML — which is what makes the page look right
 * before any JavaScript runs, and what search engines read — and swap to
 * whatever the admin has uploaded once the catalogue arrives. Same
 * static-first, live-over-the-top shape the product page already uses for
 * price and stock.
 */
export function useCatalogThumbnails(): ReadonlyMap<string, ApiProductImage> {
  const [images, setImages] = useState<ReadonlyMap<string, ApiProductImage>>(empty);

  useEffect(() => {
    let cancelled = false;
    void loadThumbnails().then((m) => {
      if (!cancelled) setImages(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return images;
}

const empty: ReadonlyMap<string, ApiProductImage> = new Map();

/**
 * The first Additional photo that is a still, for a package that has nothing
 * else of its own to put in a grid. Never a video: no card can show one.
 */
export function firstAdditionalStill(product: Pick<ApiProduct, "images">): ApiProductImage | undefined {
  return (product.images ?? []).find((i) => i.isAdditional && i.mediaType !== "video");
}

/**
 * The strip the product page shows for the current selection.
 *
 * The product's own ladder first, then the additional photos, whatever colour
 * is picked: the first frame is always the colour the shopper chose.
 *
 * A package with no colour photographs of its own leads with its additional
 * photos instead — they are the ones the admin shot of the package — and then
 * borrows every included product's gallery: a bundle is its contents, and an
 * empty frame above "Termasuk dalam paket" is worse than a busy one. Borrowed
 * first buried the package's own photos behind every colourway of every item.
 */
export function galleryForSelection(
  product: Pick<ApiProduct, "images">,
  selected: Pick<ApiSku, "packageItems"> | undefined,
  top: string | undefined,
  leg: string | undefined,
): ApiProductImage[] {
  const own = imagesForColor(product, top, leg);
  const additional = (product.images ?? []).filter((i) => i.isAdditional);
  if (own.length > 0) return [...own, ...additional];
  const borrowed = (selected?.packageItems ?? []).flatMap((item) => item.sku?.product?.images ?? []);
  return [...additional, ...borrowed];
}

/** The thumbnail for an add-on card: the accessory product's own primary image. */
export function addonThumbnail(addon: {
  sku?: { product?: Pick<ApiProduct, "images"> };
}): ApiProductImage | undefined {
  return addon.sku?.product ? primaryImage(addon.sku.product) : undefined;
}

/** Keeps a slide index inside a gallery that may have just shrunk. */
export function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(index, 0), length - 1);
}
